import React from 'react';
import { NavLink } from 'react-router-dom';
import { getAllGroups, getGroupsList } from '../../../../models/groups';
import { getOrganizationList } from '../../../../models/organization';
import i18n from '../../../../i18n';
import { SideBar } from '../../components/Sidebar/Sidebar';
import { formatDate } from '../../../utils/common';
import HeaderBanner from '../../../common/HeaderBanner';
import FilterSection from '../../../common/Filter';
import Dropdown from '../../../common/DropDown';
import Select from 'react-select';
import { selectStyles } from '../../../../styles/components/js/selectStyle';
import DataTable from 'react-data-table-component';
import { tableStyles } from '../../../../styles/components/js/tableStyle';
import { TableSkeleton } from '../../../common/skelton/TableSkelton';
import NoData from '../../../common/NoData';
import StatusBadge from '../../../common/StatusBadge';
import { Edit, Trash2, AlertCircle } from 'lucide-react';

const GROUP_STATUS = {
  'A': { label: 'Active', class: 'active' },
  'P': { label: 'Previous', class: 'previous' },
  'D': { label: 'Draft', class: 'draft' }
};

export class GroupList extends React.Component {
  constructor() {
    super();
    this.state = {
      search: {
        status: '',
        client: null,
        search_text: '',
      },
      clients: [],
      groups: [],
      isLoading: false,
      limit: 15,
      page: 1,
      total: 0,
      hideSidebar: false,
      selectedGroups: [],
      toggleCleared: false,
    };

    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.onChangePage = this.onChangePage.bind(this);
    this.onChangeRowsPerPage = this.onChangeRowsPerPage.bind(this);
    this.handleRowSelected = this.handleRowSelected.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    
    // Parse URL query params
    const params = new URLSearchParams(window.location.search);
    const search = {
      status: params.get('status') || '',
      client: params.get('client') || null,
      search_text: params.get('search') || '',
    };
    const page = parseInt(params.get('page')) || 1;
    const limit = parseInt(params.get('limit')) || 15;

    this.setState({ search, page, limit }, () => {
      this.getAllOrganizations();
      this.loadGroups();
    });
  }

  // Update URL without refreshing page
  updateUrlQuery() {
    const { search, page, limit } = this.state;
    const queryParams = {
      status: search.status || '',
      client: search.client || '',
      search: search.search_text || '',
      page: page > 1 ? page : '',
      limit: limit !== 15 ? limit : ''
    };

    // Remove empty values
    const filteredParams = Object.fromEntries(
      Object.entries(queryParams).filter(([_, v]) => v !== '')
    );

    const queryString = new URLSearchParams(filteredParams).toString();
    const url = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    window.history.replaceState(null, '', url);
  }

  async getAllOrganizations() {
    try {
      const response = await getOrganizationList();
      if (response.success) {
        const clients = response.data.map(item => ({
          value: item.id,
          label: item.organization_name
        }));
        this.setState({ clients });
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  }

  async loadGroups() {
    this.setState({ isLoading: true });
    
    const { search, page, limit } = this.state;
    
    // Build query parameters
    const queryParams = {
      page,
      limit,
      ...(search.status && { status: search.status }),
      ...(search.client && { organization: search.client }),
      ...(search.search_text && { search: search.search_text })
    };

    try {
      const response = await getAllGroups(queryParams);
      if (response.success) {
        this.setState({
          groups: response.data,
          total: response.meta?.total || response.data.length,
          isLoading: false
        });
        this.updateUrlQuery();
      } else {
        this.setState({
          groups: [],
          total: 0,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      this.setState({
        groups: [],
        total: 0,
        isLoading: false
      });
    }
  }

  handleSearchChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prevState) => ({
        search: {
          ...prevState.search,
          [name]: value
        }
      }),
      () => {
        // If searching by text, reset to page 1
        if (name === 'search_text') {
          this.setState({ page: 1 }, () => {
            this.loadGroups();
          });
        }
      }
    );
  };

  handleSearch() {
    this.setState({ page: 1 }, () => {
      this.loadGroups();
    });
  }

  handleClear() {
    const defaultSearch = {
      status: '',
      client: null,
      search_text: '',
    };
    
    this.setState({
      search: defaultSearch,
      page: 1,
      selectedGroups: []
    }, () => {
      this.loadGroups();
    });
  }

  onChangePage(page) {
    this.setState({ page }, () => {
      this.loadGroups();
    });
  }

  onChangeRowsPerPage(newLimit, page) {
    this.setState({ limit: newLimit, page: 1 }, () => {
      this.loadGroups();
    });
  }

  handleRowSelected({ selectedRows }) {
    this.setState({ selectedGroups: selectedRows });
  }

  handleDelete = async (groupId) => {
    if (window.confirm(i18n.t('confirm_delete_group'))) {
      try {
        // You'll need to import and use deleteGroup function here
        // const response = await deleteGroup(groupId);
        // if (response.success) {
        //   this.loadGroups();
        // }
        console.log('Delete group:', groupId);
      } catch (error) {
        console.error('Error deleting group:', error);
      }
    }
  };

  getColumns = () => {
    return [
      {
        id: 1,
        name: i18n.t('group_id'),
        width: '100px',
        sortable: true,
        wrap: true,
        cell: (row) => <span className="highlight-table-value">{row.id}</span>,
      },
      {
        id: 2,
        name: i18n.t('group_name'),
        minWidth: '150px',
        cell: (row) => (
          <div className="group-name-cell">
            <div className="group-names">
              <span className="name-en">{row.group_name}</span>
              <span className="name-ar">{row.group_name_ar || ''}</span>
            </div>
          
          </div>
        ),
      },
      {
        id: 3,
        name: i18n.t('client'),
        width: '120px',
        cell: (row) => (
          <span>{row.organization_info?.organization_name || 'N/A'}</span>
        ),
      },
      {
        id: 4,
        name: i18n.t('auctions'),
        width: '90px',
        center: true,
        cell: (row) => (
          <span className="badge badge-secondary">{row.total_auctons || 0}</span>
        ),
      },
      {
        id: 5,
        name: i18n.t('registration_period'),
        width: '160px',
        cell: (row) => (
          <div className="date-range">
            <div>{formatDate(row?.reg_start_date)}</div>
            <div>to</div>
            <div>{formatDate(row.reg_end_date)}</div>
          </div>
        ),
      },
      {
        id: 6,
        name: i18n.t('auction_period'),
        width: '160px',
        cell: (row) => (
          <div className="date-range">
            <div>{formatDate(row.start_date)}</div>
            <div>to</div>
            <div>{formatDate(row.end_date)}</div>
          </div>
        ),
      },
     
      {
        id: 8,
        name: i18n.t('actions'),
        width: '120px',
        cell: (row) => (
          <div className="action-buttons">
            <NavLink
              to={`/edit_group/${row.id}`}
              className="btn-new btn-new--action-b"
              title="Edit"
            >
              <Edit size={16} />
            </NavLink>
            
            {row.can_delete ? (
              <button
                className="btn-new btn-new--action-b"
                onClick={() => this.handleDelete(row.id)}
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            ) : (
              <button
                className="btn-new btn-new--action-b"
                disabled
                title="Linked to auction - Cannot delete"
              >
                <AlertCircle size={16} />
              </button>
            )}
          </div>
        ),
      },
    ];
  };

  render() {
    const {
      groups,
      clients,
      isLoading,
      search,
      page,
      limit,
      total,
      hideSidebar,
      selectedGroups
    } = this.state;

    return (
      <React.Fragment>
        <HeaderBanner title={i18n.t('groups')} />
        
        <section className="dashboard-section mt-5">
          <div className="container">
            <div className="row">
              {/* Sidebar */}
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!hideSidebar && <SideBar />}
              </div>
              
              {/* Main Content */}
              <div className={hideSidebar ? 'col-xl-12 col-lg-12 col-md-12' : 'col-xl-9 col-lg-12 col-md-12'}>
                <div className="card">
                  {/* Card Header with Expand/Collapse */}
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => {
                          this.setState({
                            hideSidebar: !hideSidebar,
                          });
                        }}
                      >
                        {hideSidebar ? <i className="icon-size-actual"></i> : <i className="icon-size-fullscreen"></i>}
                      </span>
                      <h3 className="card-title">{i18n.t('groups_list')}</h3>
                      <span className="badge badge-primary ml-2">{total} groups</span>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="card-body">
                    {/* Filter Section */}
                    <FilterSection
                      loading={isLoading}
                      onSearch={this.handleSearch}
                      onClear={this.handleClear}
                    >
                      {/* Status Filter */}
                      <Dropdown
                        name="status"
                        value={search.status}
                        onChange={this.handleSearchChange}
                        placeholder={i18n.t('status')}
                        empty={i18n.t('all_status')}
                        options={[
                          { value: 'A', label: i18n.t('active') },
                          { value: 'P', label: i18n.t('previous') },
                          { value: 'D', label: i18n.t('draft') },
                        ]}
                      />

                      {/* Client Filter */}
                      <div className="react-select-wrapper">
                        <Select
                          options={clients}
                          value={clients.find(c => c.value === search.client) || null}
                          onChange={(selectedOption) => {
                            this.setState(
                              (prevState) => ({
                                search: {
                                  ...prevState.search,
                                  client: selectedOption ? selectedOption.value : null,
                                },
                              }),
                              () => {
                                this.handleSearch();
                              }
                            );
                          }}
                          name="client"
                          styles={selectStyles}
                          menuPlacement="auto"
                          menuPortalTarget={document.body}
                          placeholder={i18n.t('select_client')}
                          isClearable
                        />
                        <label className="react-select-label">{i18n.t('client_name')}</label>
                      </div>

                      {/* Search Text Input */}
                      <div className="form-group">
                        <input
                          type="text"
                          name="search_text"
                          value={search.search_text}
                          onChange={this.handleSearchChange}
                          placeholder={i18n.t('search_by_group_name')}
                          className="form-control"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              this.handleSearch();
                            }
                          }}
                        />
                      </div>
                    </FilterSection>

                    {/* Selected Groups Info */}
                    {selectedGroups.length > 0 && (
                      <div className="selected-info mb-3">
                        <span className="badge badge-info">
                          {selectedGroups.length} {i18n.t('selected')}
                        </span>
                      </div>
                    )}

                    {/* Data Table */}
                    <div className="Data-Listing">
                      {isLoading ? (
                        <TableSkeleton columns={8} rows={10} />
                      ) : groups.length ? (
                        <DataTable
                          responsive={true}
                          columns={this.getColumns()}
                          data={groups}
                          customStyles={tableStyles}
                          highlightOnHover
                          selectableRows
                          selectableRowsHighlight
                          onSelectedRowsChange={this.handleRowSelected}
                          clearSelectedRows={this.state.toggleCleared}
                          pagination
                          paginationServer
                          paginationTotalRows={total}
                          paginationPerPage={limit}
                          paginationDefaultPage={page}
                          paginationRowsPerPageOptions={[15, 25, 50, 100]}
                          paginationComponentOptions={{
                            rowsPerPageText: i18n.t('rows_per_page'),
                            rangeSeparatorText: i18n.t('out'),
                          }}
                          onChangePage={this.onChangePage}
                          onChangeRowsPerPage={this.onChangeRowsPerPage}
                          noDataComponent={
                            <NoData
                              message={i18n.t('no_groups_found')}
                              actionText={i18n.t('clear_filters')}
                              onAction={this.handleClear}
                            />
                          }
                        />
                      ) : (
                        <NoData
                          message={i18n.t('no_groups_available')}
                          actionText={i18n.t('refresh')}
                          onAction={this.loadGroups.bind(this)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}

// Helper components
const ActionButton = ({ label, icon, to, variant = 'info', onClick, active = false }) => {
  if (to) {
    return (
      <NavLink
        to={to}
        className={`btn-new btn-new--action`}
        style={{ whiteSpace: 'nowrap', cursor: 'pointer', marginRight: '4px' }}
      >
        {icon} {i18n.t(label)}
      </NavLink>
    );
  }

  return (
    <button
      disabled={active}
      className={`btn-new btn-new--action`}
      style={{ whiteSpace: 'nowrap', cursor: 'pointer', marginRight: '4px' }}
      onClick={onClick}
    >
      {icon} {i18n.t(label)}
    </button>
  );
};